import type { PropsWithChildren } from 'react'
import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextButton from '../../../../LuxKit/Button/TextButton'
import TextLink from '../../../../LuxKit/TextLink'
import { useIntl, FormattedMessage } from 'react-intl'

const ErrorContainer = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${rem(32)} ${rem(12)};

  > * + * {
    margin-top: ${rem(20)};
  }
`
const StyledHeading = styled(Heading)`
  max-width: ${rem(700)};
`

const StyledSubtitle = styled(BodyText)`
  max-width: ${rem(700)};
`

interface Props {
  className?: string
  onRefresh?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const onRefreshFn = () => window.location.reload()

function OhNoRefreshError(props: PropsWithChildren<Props>) {
  const {
    children,
    onRefresh = onRefreshFn,
    className,
  } = props
  const intl = useIntl()

  return (
    <ErrorContainer className={className}>
      <LineExclamationCircleIcon size="XL" />
      <StyledHeading variant="heading2" align="center" forwardedAs="h1">
        {intl.formatMessage({
          id: 'hpm9HOV1JX',
          defaultMessage: 'Oh no, an error has occurred',
          description: 'Error page heading when something goes wrong',
        })}
      </StyledHeading>
      <StyledSubtitle variant="large" align="center" forwardedAs="h2">
        {children}
        {!children && <FormattedMessage
          id="b+2q1lcKOs"
          defaultMessage="Our team are working on fixing it now. Please try refreshing the page or visiting our <link>homepage</link> for our latest offers."
          description="Error page body text encouraging the user to refresh or visit the homepage"
          values={{
            link: (chunks) => <TextLink href="/" target="_blank" rel="noopener nofollow">{chunks}</TextLink>,
          }}
        />}
      </StyledSubtitle>
      <TextButton
        kind="primary"
        onClick={onRefresh}
        fit="mobile-full-width"
      >
        {intl.formatMessage({
          id: '3SwPzn5sUW',
          defaultMessage: 'Refresh',
          description: 'Button to refresh the page after an error',
        })}
      </TextButton>
    </ErrorContainer>
  )
}

export default OhNoRefreshError
